import {
  FirebaseAuthTypes,
  getIdToken,
  onAuthStateChanged as onAuthStateChangedMobile,
  signOut as signOutMobile
} from '@react-native-firebase/auth';
import { FirebaseFirestoreTypes } from '@react-native-firebase/firestore';
import { doc as docMobile, onSnapshot as onSnapshotMobile } from '@react-native-firebase/firestore';
import { FirebaseMessagingTypes, getToken } from '@react-native-firebase/messaging';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { router } from 'expo-router';
import {
  Auth,
  getIdToken as getIdTokenWeb,
  onAuthStateChanged as onAuthStateChangedWeb,
  signOut as signOutWeb,
  User
} from 'firebase/auth';
import { doc, DocumentData, Firestore, onSnapshot } from 'firebase/firestore';
import { getToken as getTokenWeb, Messaging, onMessage as onMessageWeb } from 'firebase/messaging';
import { useEffect, useRef, useState } from 'react';
import { BackHandler, Platform } from 'react-native';
import * as Linking from 'expo-linking';

import { client } from '@/api';
import { useSignInWithEmail } from '@/api/hooks/authQueryHooks';
import { useGetMeMutation } from '@/api/hooks/meHooks';
import { useRegisterFCMToken } from '@/api/hooks/notificationsHooks';
import { firebaseConfig } from '@/constants/config';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useMeFavorites } from '@/contexts/favoritesContext';
import { auth, db, messaging } from '@/firebaseConfig';
import { safeJsonParse } from '@/utils/jsonParse';
import { RoomData } from '@/api/models/chatRoomModels';
import { setPendingLink } from '@/deepLinks/deepLinkStore';
import { parseNativeDeepLink, parseNativeDeepLinkWithParams } from '@/deepLinks/parseDeepLink';
import { parseQueryParams } from '@/utils/parseQueryParams';

const splashScreenDuration = 10000;

export const useAppReadyFunctionality = (pathname?: string) => {
  const { dispatch, setPreferences, setChurchPreferences, setSecularPreferences } = useAuth();
  const { showError } = useEndFlowModal();

  const { mutateAsync: getMe } = useGetMeMutation();
  const { mutateAsync: signInEmail } = useSignInWithEmail();
  const { mutateAsync: registerFCMToken } = useRegisterFCMToken();
  const { fetchFavorites } = useMeFavorites();

  const [appIsReady, setAppIsReady] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState<boolean>(false);
  const showMaintenanceModalRef = useRef(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    // Check if app opened from a deep link (Web only)
    if (Platform.OS === 'web') {
      if ('serviceWorker' in navigator) {
        (async () => {
          const messagingWebObtained = await messaging();

          if (!messagingWebObtained) return;

          onMessageWeb(messagingWebObtained as Messaging, (payload) => {
            const notification = new Notification(payload.notification?.title ?? '', {
              icon: '/assets/images/icon.png',
              body: payload.notification?.body
            });
            if (payload.data?.roomData) {
              const roomJson = safeJsonParse<RoomData>(payload.data.roomData.toString());

              notification.onclick = () =>
                router.navigate({
                  pathname: '/(app)/messages',
                  params: {
                    roomId: roomJson?.id
                  }
                });
            }
          });
        })();
      }
      if (
        pathname !== '/' &&
        pathname !== '/home' &&
        pathname !== '' &&
        !pathname?.includes('login') &&
        pathname !== undefined
      ) {
        const params = parseQueryParams(pathname);
        const deepLink = parseNativeDeepLinkWithParams(pathname, params);
        if (deepLink) setPendingLink(deepLink);
      }
    }
  }, [pathname]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      // 1. Get the initial URL that opened the app (cold-start deep link)
      Linking.getInitialURL().then((url) => {
        if (url) {
          if (__DEV__) console.log('[Cold-Start DeepLink]:', url);
          try {
            const parsed = Linking.parse(url);
            if (parsed.path) {
              const cleanPath = parsed.path.startsWith('/') ? parsed.path : `/${parsed.path}`;
              const deepLink = parseNativeDeepLinkWithParams(
                cleanPath,
                (parsed.queryParams as Record<string, string>) ?? {}
              );
              if (deepLink) setPendingLink(deepLink);
            }
          } catch (e) {
            console.error('Failed to parse initial URL:', e);
          }
        }
      });

      // 2. Check if app opened from a notification (quit state)
      (async () => {
        const messagingMobile = (await messaging()) as FirebaseMessagingTypes.Module;
        messagingMobile.getInitialNotification().then((remoteMessage) => {
          if (remoteMessage) {
            if (__DEV__) console.log('[Notification from Quit State]:', remoteMessage.data);
            if (remoteMessage?.data?.roomData) {
              const roomData = remoteMessage?.data?.roomData;
              const deepLink = parseNativeDeepLinkWithParams('/(app)/messages', { roomData: roomData.toString() });
              if (deepLink) setPendingLink(deepLink);
            } else if (remoteMessage.data?.path) {
              const screen = remoteMessage.data?.path;
              if (screen !== null && screen && typeof screen === 'string') {
                const deepLink = parseNativeDeepLink(screen);
                if (deepLink) setPendingLink(deepLink);
              }
            }
          }
        });
      })();
    }
  }, []);

  const getAuthStatus = (): Promise<number> =>
    new Promise((resolve) => {
      const splashTimeout = 10000;
      let splashTimeoutTimer: number;
      if (!appIsReady) {
        splashTimeoutTimer = setTimeout(() => {
          resolve(Date.now());
        }, splashTimeout);
      }

      const signOutResolve = async ({
        splashTimeoutTimer,
        authFinishTime
      }: {
        splashTimeoutTimer: number;
        authFinishTime: number;
      }) => {
        dispatch({ type: 'SIGN_OUT' });
        clearTimeout(splashTimeoutTimer);
        if (Platform.OS === 'web') {
          await signOutWeb(auth() as Auth);
        } else {
          await signOutMobile(auth() as FirebaseAuthTypes.Module);
        }
        showError({});
        if (router.canGoBack()) {
          router.dismissAll();
          router.push('/');
        }
        resolve(authFinishTime);
      };

      const authStateChangeObserver = async (fbUser: FirebaseAuthTypes.User | User | null) => {
        const authFinishTime = Date.now();
        if (fbUser) {
          const token =
            Platform.OS === 'web'
              ? await getIdTokenWeb(auth().currentUser as User)
              : await getIdToken(auth().currentUser as FirebaseAuthTypes.User);

          if (__DEV__) console.log('FBUSER: ', fbUser);
          if (__DEV__) console.log('Bearer', token);

          client.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // Get favorites for user once user it's authenticated and not before
          fetchFavorites();

          // Get FCM Token and send it to backend after setting Authorization
          const getTokenAndRegister = async () => {
            try {
              // Service worker URL w/config variables
              if (Platform.OS === 'web') {
                const swUrl = `/firebase-messaging-sw.js?${firebaseConfig}`;
                const registration = await navigator.serviceWorker.register(swUrl);
                const messagingWebObtained = await messaging();

                if (!messagingWebObtained) return;

                const token = await getTokenWeb(messagingWebObtained, {
                  vapidKey: process.env.EXPO_PUBLIC_FB_VAPID_KEY,
                  serviceWorkerRegistration: registration
                });
                if (__DEV__) console.log('FCM TOKEN', token);
                if (token) {
                  registerFCMToken(
                    { token },
                    {
                      onError: (e) => {
                        console.warn(e);
                      }
                    }
                  );
                }
              } else {
                // Mobile token
                const messagingObtained = (await messaging()) as FirebaseMessagingTypes.Module;
                const token = await getToken(messagingObtained);
                if (__DEV__) console.log('FCM TOKEN', token);
                if (token) {
                  registerFCMToken(
                    { token },
                    {
                      onError: (e) => {
                        console.warn(e);
                      }
                    }
                  );
                }
              }
            } catch (e) {
              console.error(e);
            }
          };

          getTokenAndRegister();

          if (fbUser.phoneNumber !== null && fbUser.providerData[0].providerId === 'phone') {
            const phone = fbUser.phoneNumber;

            // Individual User
            await getMe(
              { firebaseId: fbUser.uid },
              {
                onSuccess: (data) => {
                  if (!data?.user) {
                    dispatch({
                      type: 'SIGN_IN',
                      userId: fbUser.uid,
                      providerId: fbUser.providerData[0].providerId,
                      phone
                    });
                    clearTimeout(splashTimeoutTimer);
                    resolve(authFinishTime);
                  } else if (data?.user) {
                    dispatch({
                      type: 'SIGN_IN',
                      userId: fbUser.uid,
                      providerId: fbUser.providerData[0].providerId,
                      phone,
                      user: {
                        ...data,
                        user: {
                          ...data.user,
                          location: data.onBoardingPreference?.location ?? '',
                          searchRange: data.onBoardingPreference?.searchRange ?? 100,
                          onBoardingId: data.onBoardingPreference?.id ?? '',
                          address: data.onBoardingPreference?.address ?? ''
                        }
                      }
                    });

                    setPreferences({
                      programTypes: data.onBoardingPreference?.programTypes ?? [],
                      denominationTypes: data.onBoardingPreference?.denominationTypes ?? [],
                      religionTypes: data.onBoardingPreference?.religionTypes ?? []
                    });
                    clearTimeout(splashTimeoutTimer);
                    resolve(authFinishTime);
                  }
                },
                onError: (e) => {
                  const axiosError = e as AxiosError;
                  console.error(axiosError.response);
                  signOutResolve({ splashTimeoutTimer, authFinishTime });
                }
              }
            );
          } else if (fbUser.providerData[0].providerId === 'password') {
            // NPO User
            await getMe(
              {
                firebaseId: fbUser.uid,
                onBoardingType: 'npo'
              },
              {
                onSuccess: (data) => {
                  if (!data?.user) {
                    dispatch({
                      type: 'NPO_SIGN_IN',
                      userId: fbUser.uid,
                      providerId: fbUser.providerData[0].providerId,
                      email: fbUser.email ?? ''
                    });
                    clearTimeout(splashTimeoutTimer);
                    resolve(authFinishTime);
                  } else if (data?.user) {
                    dispatch({
                      type: 'NPO_SIGN_IN',
                      userId: fbUser.uid,
                      providerId: fbUser.providerData[0].providerId,
                      email: fbUser.email ?? '',
                      user: {
                        ...data,
                        user: {
                          ...data.user,
                          location: data.onBoardingPreference?.location ?? '',
                          searchRange: data.onBoardingPreference?.searchRange ?? 100,
                          onBoardingId: data.onBoardingPreference?.id ?? '',
                          address: data.onBoardingPreference?.address ?? ''
                        }
                      }
                    });
                    // User Loggued
                    signInEmail(fbUser.email ?? '', {
                      onSuccess: (loginData) => {
                        const type = loginData.type;
                        const preferences =
                          type === 'CHARITIES' ? data.onBoardingPreferenceSecular : data.onBoardingPreferenceChurch;

                        dispatch({
                          type: 'SET_SIGN_UP_DATA',
                          signUpData: {
                            loginEmail: loginData
                          },
                          preferenceId: preferences?.at(0)?.id ?? ''
                        });

                        if (type === 'CHARITIES') {
                          setSecularPreferences({
                            programTypes: data.onBoardingPreferenceSecular?.at(0)?.programTypes ?? []
                          });
                        } else if (type === 'CHURCH') {
                          setChurchPreferences({
                            programTypes: data.onBoardingPreferenceChurch?.at(0)?.programTypes ?? [],
                            denominationTypes: data.onBoardingPreferenceChurch?.at(0)?.denominationTypes ?? [],
                            religionTypes: data.onBoardingPreferenceChurch?.at(0)?.religionTypes ?? []
                          });
                        }

                        clearTimeout(splashTimeoutTimer);
                        resolve(authFinishTime);
                      },
                      onError: (e: any) => {
                        console.warn('ERROR', e);
                        signOutResolve({ splashTimeoutTimer, authFinishTime });
                      }
                    });
                  }
                },
                onError: (e) => {
                  console.warn('ERROR', e);
                  signOutResolve({ splashTimeoutTimer, authFinishTime });
                }
              }
            );
          }
        } else {
          dispatch({ type: 'SIGN_OUT' });
          queryClient.removeQueries();
          resolve(authFinishTime);
        }
      };

      if (Platform.OS === 'web') {
        onAuthStateChangedWeb(auth() as Auth, authStateChangeObserver);
      } else {
        onAuthStateChangedMobile(auth() as FirebaseAuthTypes.Module, authStateChangeObserver);
      }
    });

  const appReadyFunctionality = async (): Promise<void> => {
    const authFinishTime = await getAuthStatus();
    const authStartTime = Date.now();
    const authDuration = authFinishTime - authStartTime;
    const fastAuth = authDuration < splashScreenDuration;

    if (fastAuth) {
      setTimeout(() => setAppIsReady(true), splashScreenDuration);
    } else {
      setAppIsReady(true);
    }

    dispatch({ type: 'AUTH_READY' });
  };

  // ToDO: In the future we could any other app settings in here
  // Maintenance mode logic
  const onMaintenanceResult = (
    documentSnapshot: FirebaseFirestoreTypes.DocumentSnapshot<FirebaseFirestoreTypes.DocumentData> | DocumentData
  ) => {
    if (documentSnapshot.exists) {
      const maintenanceMode = documentSnapshot.data() as { maintenance_mode: boolean };
      setShowMaintenanceModal(maintenanceMode?.maintenance_mode);
    }
  };

  const onMaintenanceError = (error: Error) => {
    console.error(error);
  };

  const onBackPressWhileMaintenance = () => {
    return showMaintenanceModalRef.current;
  };

  useEffect(() => {
    showMaintenanceModalRef.current = showMaintenanceModal;
  }, [showMaintenanceModal]);

  useEffect(() => {
    appReadyFunctionality();

    if (Platform.OS === 'web') {
      //Web
      const maintenanceStatusListener = onSnapshot(
        doc(db() as Firestore, 'settings', 'app'),
        onMaintenanceResult,
        onMaintenanceError
      );

      //Stop listening to app settings
      return () => maintenanceStatusListener();
    } else {
      //Mobile
      const maintenanceStatusListener = onSnapshotMobile(
        docMobile(db() as FirebaseFirestoreTypes.Module, 'settings', 'app'),
        onMaintenanceResult,
        onMaintenanceError
      );

      if (Platform.OS === 'android') {
        BackHandler.addEventListener('hardwareBackPress', onBackPressWhileMaintenance);
      }

      return () => {
        maintenanceStatusListener();
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    showMaintenanceModal,
    appIsReady
  };
};
